import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Drawer, Flex, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  InfoCircleOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import './RolesAndPermissions.css';
import { useSpin } from '@/components/SpinContext.jsx';
import { useToken } from '@/common/helper';
import { CustomButton, CustomParagraph, CustomTitle } from '@/components';
import CommonDrawer from '@/components/CommonDrawer';
import AddNewRoleForm from './AddNewRoleForm';
import { Form as AntForm } from 'antd';
import SectionsTable from './SectionsTable';
import FeaturesTable from './FeatureTable';
import {
  GetRoles,
  ChangeRolePermission,
  GetPermissionsOptions,
  ClearCache,
} from '@/apis/roles.js';
import { fetchMenu } from '@/redux-toolkit/slice/menuSlice.js';
import { setShouldShowRolePreview } from '@/redux-toolkit/slice/previewSlice';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { getPermissions as getMenuPermission } from '@/common/helper/getPermissions';
import {
  isBaseLocked,
  optionsForSection,
  optionsForSubFeature,
} from './utils/permissions';
import AddButton from '@/components/AddButton';
import {
  CopyProfileIcon,
  FeaturesRoles,
  SectionsRoles,
} from '@/assets/icons/icons';

import {
  PermissionValue,
  RoleModel,
  RootState,
  toPermissionMatrix,
  ApiEnvelope,
  HttpResponse,
  EditRoleFormData,
} from './utils/types';
import {
  ApiResponse,
  ErrorLike,
  FeaturePermissionMatrix,
  PermissionsApiResponse,
} from '@/apis/types';

import type {
  AlertThreshold,
  SelectedThresholdValues,
} from '@/pages/Shipments/PerShipmentNotification/components/AlertFieldByType';
import { useFetchAlertThresholds } from '@/hooks';
import { DecodedToken } from '@/common/helper/useToken';

type Scope = 'SECTION' | 'FEATURE';

type GetRolesResponse = HttpResponse<ApiEnvelope<RoleModel[]>>;
type AddNewRoleFormValues = { name: string };
const FIRST_COL_WIDTH = 320;

type ShipmentStatusOption = {
  option_key: string;
  label: string;
  category?: string;
};

type ShipmentStatusOptionsBlock = {
  select_all_by_default: boolean;
  options: ShipmentStatusOption[];
};

type KpiGroupItem = { id: string; label: string };
type KpiGroup = { id: string; label: string; items: KpiGroupItem[] };

type HomeKpiOptionsBlock = {
  select_all_by_default: boolean;
  groups: KpiGroup[];
};

type KpiReportGroup = { id: string; label: string; items: unknown[] };

type HomeKpiReportsOptionsBlock = {
  select_all_by_default: boolean;
  groups: KpiReportGroup[];
};

type PermissionOptionsPayload = {
  'Shipment status'?: ShipmentStatusOptionsBlock;
  'Home KPI charts'?: HomeKpiOptionsBlock;
  'Home KPI reports'?: HomeKpiReportsOptionsBlock;
};

type PermissionOptionsResponse = HttpResponse<
  ApiEnvelope<PermissionOptionsPayload>
>;

const buildSelectedThresholdValues = (
  allowedTypes: string[],
  config: AlertThreshold[]
): SelectedThresholdValues => {
  const out: SelectedThresholdValues = {};
  if (!Array.isArray(allowedTypes) || allowedTypes.length === 0) return out;

  for (const t of allowedTypes) {
    let foundValue: unknown = undefined;
    for (const group of config) {
      const rule = group.alerts.find((a) => a.alert_type === t);
      if (rule) {
        foundValue = rule.threshold_value;
        break;
      }
    }
    out[t] = foundValue as never;
  }
  return out;
};

const countKpiItems = (groups: KpiGroup[]) =>
  (groups ?? []).reduce((acc, g) => acc + (g.items?.length ?? 0), 0);

const RolesAndPermissions: React.FC = () => {
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const [rolesInfoCollapsed, setRolesInfoCollapsed] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const infoCardRef = useRef<HTMLDivElement | null>(null);
  const [infoCardHeight, setInfoCardHeight] = useState<number | undefined>(
    undefined
  );

  const { alertThreshold } = useFetchAlertThresholds(null);
  const alertThresholdConfig: AlertThreshold[] = React.useMemo(
    () =>
      Array.isArray(alertThreshold)
        ? alertThreshold.map((group) => ({
            ...group,
            phase_display: group.phase_display ?? '',
            alerts: Array.isArray(group.alerts)
              ? group.alerts.map((alert) => ({
                  unit: '',
                  alert_name: alert.alert_name ?? '',
                  ...alert,
                }))
              : [],
          }))
        : [],
    [alertThreshold]
  );

  const alertsReady = useMemo(() => {
    const count =
      alertThresholdConfig?.reduce(
        (acc, g) => acc + (g.alerts?.length ?? 0),
        0
      ) ?? 0;
    return count > 0;
  }, [alertThresholdConfig]);
  const [roleForm] = AntForm.useForm<AddNewRoleFormValues>();
  const main_menu = useSelector((s: RootState) => s.menu.items);
  const rawPerms = getMenuPermission(
    main_menu as never,
    'Settings',
    'Roles & Permissions'
  );

  const RolesPermissions: FeaturePermissionMatrix =
    toPermissionMatrix(rawPerms);

  const dispatch = useDispatch();
  const { token, removeToken, decodedUserToken } = useToken() as {
    token: string | null;
    removeToken: () => void;
    decodedUserToken: () => DecodedToken | null;
  };
  const decodeToken = decodedUserToken();
  const isDefaultAdmin = decodeToken?.is_company_owner === true;
  const showPreviewMode = decodeToken?.preview_mode?.toLowerCase() !== 'role';
  const { isMobile } = useBreakpoints();

  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpenData, setFormOpenData] = useState<EditRoleFormData | null>(
    null
  );
  const [roleList, setRoleList] = useState<RoleModel[]>([]);

  const [statusOpenRoleId, setStatusOpenRoleId] = useState<string | null>(null);
  const [statusTempSelected, setStatusTempSelected] = useState<string[]>([]);
  const [alertsOpenRoleId, setAlertsOpenRoleId] = useState<string | null>(null);
  const [alertsTempSelected, setAlertsTempSelected] =
    useState<SelectedThresholdValues>({});
  const [selectAllAlertTypes, setSelectAllAlertTypes] = useState(false);

  const [kpiOpenRoleId, setKpiOpenRoleId] = useState<string | null>(null);
  const [kpiTempSelected, setKpiTempSelected] = useState<string[]>([]);
  const [selectAllKpis, setSelectAllKpis] = useState(false);
  const [kpiTotalCount, setKpiTotalCount] = useState<number>(0);

  const [shipmentStatusOptions, setShipmentStatusOptions] = useState<
    ShipmentStatusOption[]
  >([]);
  const [shipmentStatusSelectAllDefault, setShipmentStatusSelectAllDefault] =
    useState(false);

  const [kpiGroups, setKpiGroups] = useState<KpiGroup[]>([]);
  const [kpiSelectAllDefault, setKpiSelectAllDefault] = useState(true);

  const [kpiReportsOpenRoleId, setKpiReportsOpenRoleId] = useState<
    string | null
  >(null);
  const [kpiReportsTempSelected, setKpiReportsTempSelected] = useState<
    string[]
  >([]);
  const [kpiReportsLabels, setKpiReportsLabels] = useState<string[]>([]);
  const [kpiReportsLabelToId, setKpiReportsLabelToId] = useState<
    Map<string, string>
  >(new Map());
  const [kpiReportsIdToLabel, setKpiReportsIdToLabel] = useState<
    Map<string, string>
  >(new Map());
  const [kpiReportsSelectAllDefault, setKpiReportsSelectAllDefault] =
    useState(false);

  const shipmentStatusLabels = useMemo(
    () => shipmentStatusOptions.map((o) => o.label),
    [shipmentStatusOptions]
  );

  const shipmentLabelToApiKey = useMemo(() => {
    const m = new Map<string, string>();
    shipmentStatusOptions.forEach((o) => m.set(o.label, o.option_key));
    return m;
  }, [shipmentStatusOptions]);

  const shipmentApiKeyToLabel = useMemo(() => {
    const m = new Map<string, string>();
    shipmentStatusOptions.forEach((o) => m.set(o.option_key, o.label));
    return m;
  }, [shipmentStatusOptions]);

  const allKpiIds = useMemo(() => {
    const ids: string[] = [];
    kpiGroups.forEach((g) => g.items?.forEach((it) => ids.push(it.id)));
    return ids;
  }, [kpiGroups]);

  const RolesInfoText = ({
    collapsed,
    onToggleCollapse,
  }: {
    collapsed?: boolean;
    onToggleCollapse?: () => void;
  }) => {
    if (collapsed) {
      return (
        <button
          type='button'
          className='RolesAndPermissionsCard-collapseBtn'
          aria-label='Expand roles info'
          onClick={onToggleCollapse}
        >
          <VerticalRightOutlined />
        </button>
      );
    }
    return (
      <div>
        <div className='RolesAndPermissionsCard-header'>
          <CustomParagraph
            text='Give access via Roles'
            fontSize='20px'
            fontWeight={700}
            lineHeight='28px'
            color='var(--text-primary)'
            marginBottom='0'
          />
          {onToggleCollapse ? (
            <button
              type='button'
              className='RolesAndPermissionsCard-collapseBtn'
              aria-label='Collapse roles info'
              onClick={onToggleCollapse}
            >
              <VerticalLeftOutlined />
            </button>
          ) : null}
        </div>
        <p>Zoomlogi has 3 initial roles: Admin, Member & Viewer.</p>
        <br />
        <p>
          The <b>View</b> permission allows users to view the relevant piece of
          content. <b>Edit</b> allows users to edit that information (plus
          View). The <b>Create</b> option allows users to add new content (plus
          Edit and View) in that area.
        </p>
        <br />
        <p>
          <b>No access</b> bars access from that content.
        </p>
      </div>
    );
  };

  const unauthorize = useCallback(
    (error: ErrorLike) => {
      if (error?.status === 401) {
        removeToken();
        navigate('/login');
      }
    },
    [navigate, removeToken]
  );

  const fetchAllRoles = useCallback(() => {
    showSpin();
    GetRoles(token)
      .then((res: GetRolesResponse) => {
        const ok = res?.data?.data?.is_success;
        const arr: RoleModel[] = ok ? (res.data.data.data ?? []) : [];
        if (ok) setRoleList(arr);
      })
      .catch(unauthorize)
      .finally(hideSpin);
  }, [showSpin, hideSpin, token, unauthorize]);

  const fetchPermissionOptions = useCallback(() => {
    if (!token) return;

    showSpin();
    GetPermissionsOptions(token)
      .then((res: PermissionOptionsResponse) => {
        const ok = res?.data?.data?.is_success;
        const payload = ok ? res?.data?.data?.data : undefined;

        const shipmentBlock = payload?.['Shipment status'];
        const homeKpiBlock = payload?.['Home KPI charts'];

        const ssOptions = shipmentBlock?.options ?? [];
        setShipmentStatusOptions(ssOptions);
        setShipmentStatusSelectAllDefault(
          Boolean(shipmentBlock?.select_all_by_default)
        );

        const groups = homeKpiBlock?.groups ?? [];
        setKpiGroups(groups);
        setKpiSelectAllDefault(Boolean(homeKpiBlock?.select_all_by_default));
        setKpiTotalCount(countKpiItems(groups));

        const reportsBlock = payload?.['Home KPI reports'];
        const reportGroups = reportsBlock?.groups ?? [];
        const labels = reportGroups.map((g) => g.label);
        setKpiReportsLabels(labels);
        setKpiReportsSelectAllDefault(
          Boolean(reportsBlock?.select_all_by_default)
        );

        const labelToId = new Map<string, string>();
        const idToLabel = new Map<string, string>();
        reportGroups.forEach((g) => {
          labelToId.set(g?.label, g?.id);
          idToLabel.set(g?.id, g?.label);
        });
        setKpiReportsLabelToId(labelToId);
        setKpiReportsIdToLabel(idToLabel);
      })
      .catch(unauthorize)
      .finally(hideSpin);
  }, [showSpin, hideSpin, token, unauthorize]);

  useEffect(() => {
    fetchAllRoles();
    fetchPermissionOptions();

    const onResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fetchAllRoles, fetchPermissionOptions]);

  // Remember the expanded banner height so the collapsed strip matches it
  // (instead of stretching to the full Sections table height).
  useEffect(() => {
    if (rolesInfoCollapsed || !isDesktop) return;
    const el = infoCardRef.current;
    if (!el) return;

    const sync = () => setInfoCardHeight(el.offsetHeight);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rolesInfoCollapsed, isDesktop]);

  const { sectionsRows, featuresRows } = useMemo(() => {
    const allSectionNames = Array.from(
      new Set(
        roleList.flatMap((r) =>
          (r?.feature_list ?? []).map((f) => f.feature_name)
        )
      )
    );
    const sections = allSectionNames.map((name) => {
      const row: Record<string, unknown> = { key: name, feature_name: name };
      roleList.forEach((role) => {
        const f = (role?.feature_list ?? []).find(
          (x) => x.feature_name === name
        );
        (row as Record<string, unknown>)[role.id] = f
          ? f.permission?.[0]
          : 'NO_ACCESS';
      });
      return row;
    });

    const allSubNames = Array.from(
      new Set(
        roleList.flatMap((r) =>
          (r?.sub_feature_list ?? []).map((s) => s.feature_name)
        )
      )
    );

    const features = allSubNames.map((name) => {
      let desc = '';
      for (const role of roleList) {
        const s = (role?.sub_feature_list ?? []).find(
          (x) => x.feature_name === name
        );
        if (s?.description) {
          desc = s.description;
          break;
        }
      }

      const row: Record<string, unknown> & {
        allowedMap: Record<string, string[]>;
      } = {
        key: name,
        feature_name: name,
        description: desc,
        allowedMap: {},
      };

      roleList.forEach((role) => {
        const s = (role?.sub_feature_list ?? []).find(
          (x) => x.feature_name === name
        );
        (row as Record<string, unknown>)[role.id] = s
          ? s.permission?.[0]
          : 'NO_ACCESS';

        const apiVals: string[] = Array.isArray(s?.allowed_statuses)
          ? s!.allowed_statuses!
          : [];
        const featureLower = name.toLowerCase();

        let uiVals: string[];
        if (featureLower === 'shipment status') {
          uiVals = apiVals
            .map((apiKey) => shipmentApiKeyToLabel.get(apiKey) ?? '')
            .filter(Boolean);
        } else if (featureLower === 'home kpi reports') {
          uiVals = apiVals
            .map((apiKey) => kpiReportsIdToLabel.get(apiKey) ?? '')
            .filter(Boolean);
        } else {
          uiVals = apiVals;
        }

        row.allowedMap[role.id] = uiVals;
      });

      return row;
    });

    return { sectionsRows: sections, featuresRows: features };
  }, [roleList, shipmentApiKeyToLabel, kpiReportsIdToLabel]);

  useEffect(() => {
    const root = document.querySelector('.rp-table-wrap') as HTMLElement | null;
    if (!root) return;

    const strip = () => {
      root
        .querySelectorAll<HTMLElement>('.ant-select-selection-item[title]')
        .forEach((el) => el.removeAttribute('title'));
    };

    strip();

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (
          m.type === 'attributes' ||
          (m.type === 'childList' &&
            (m.addedNodes.length > 0 || m.removedNodes.length > 0))
        ) {
          strip();
          break;
        }
      }
    });

    mo.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['title', 'class'],
    });

    return () => mo.disconnect();
  }, []);

  const updatePermission = (
    scope: Scope,
    featureName: string,
    roleModel: RoleModel,
    newValue: PermissionValue
  ) => {
    const model: RoleModel = JSON.parse(JSON.stringify(roleModel));
    if (scope === 'SECTION') {
      const item = model?.feature_list?.find(
        (x) => x.feature_name === featureName
      );
      if (item) item.permission[0] = newValue;
    } else {
      const item = model?.sub_feature_list?.find(
        (x) => x.feature_name === featureName
      );
      if (item) item.permission[0] = newValue;
    }

    showSpin();
    ChangeRolePermission(token, model)
      .then((res: PermissionsApiResponse) => {
        if (res?.data?.data?.is_success) {
          message.success(res?.data?.data?.message);
          if (res?.data?.data?.access_token) {
            localStorage.setItem('@token', res?.data?.data?.access_token);
          }
          if (res?.data?.data?.refresh) {
            localStorage.setItem('@refreshToken', res?.data?.data?.refresh);
          }
        }
      })
      .then(() => {
        fetchAllRoles();
        (dispatch as unknown as (a: unknown) => unknown)(
          fetchMenu(token as never)
        );
        ClearCache();
      })
      .catch(unauthorize)
      .finally(hideSpin);
  };

  const openStatusPopover = (roleModel: RoleModel) => {
    const sub = (roleModel?.sub_feature_list ?? []).find(
      (x) => x.feature_name.toLowerCase() === 'shipment status'
    );

    const apiVals: string[] = Array.isArray(sub?.allowed_statuses)
      ? sub!.allowed_statuses!
      : [];

    const selectedUi = apiVals
      .map((apiKey) => shipmentApiKeyToLabel.get(apiKey) ?? '')
      .filter(Boolean);

    setStatusTempSelected(selectedUi);
    setStatusOpenRoleId(roleModel.id);
  };

  const closeStatusPopover = () => setStatusOpenRoleId(null);

  const resetStatus = () => {
    const roleModel = roleList.find((r) => r.id === statusOpenRoleId);
    if (!roleModel) return;

    const model: RoleModel =
      typeof structuredClone === 'function'
        ? structuredClone(roleModel)
        : JSON.parse(JSON.stringify(roleModel));

    const sub = (model?.sub_feature_list ?? []).find(
      (x) => (x?.feature_name ?? '').toLowerCase() === 'shipment status'
    );
    if (!sub) return;

    sub.allowed_statuses = [];

    showSpin();
    ChangeRolePermission(token, model)
      .then(fetchAllRoles)
      .then(() => setStatusTempSelected([]))
      .catch(unauthorize)
      .finally(hideSpin);
  };

  const applyStatus = () => {
    const roleModel = roleList.find((r) => r.id === statusOpenRoleId);
    if (!roleModel) return;

    const model: RoleModel = JSON.parse(JSON.stringify(roleModel));
    const sub = (model?.sub_feature_list ?? []).find(
      (x) => x.feature_name.toLowerCase() === 'shipment status'
    );

    if (sub) {
      const apiValues = statusTempSelected
        .map((label) => shipmentLabelToApiKey.get(label))
        .filter(Boolean) as string[];

      sub.allowed_statuses = apiValues;
    }

    showSpin();
    ChangeRolePermission(token, model)
      .then((res: ApiResponse) => {
        if (res?.data?.data?.is_success) {
          fetchAllRoles();
          message.success(res?.data?.data?.message);
        }
      })
      .catch(unauthorize)
      .finally(() => {
        hideSpin();
        setStatusOpenRoleId(null);
      });
  };

  const openAlertsPopover = (roleModel: RoleModel) => {
    const sub = (roleModel?.sub_feature_list ?? []).find(
      (x) => x.feature_name.toLowerCase() === 'alerts'
    );

    const allowedTypesRaw: string[] = Array.isArray(sub?.allowed_statuses)
      ? sub!.allowed_statuses!
      : [];

    const allTypes = Array.from(
      new Set(
        alertThresholdConfig.flatMap((g) =>
          (g.alerts ?? []).map((a) => a.alert_type).filter(Boolean)
        )
      )
    );

    const allowedTypes = allowedTypesRaw.filter((t) => allTypes.includes(t));

    const typesToUse = allowedTypes.length > 0 ? allowedTypes : [];

    const selectedMap = buildSelectedThresholdValues(
      typesToUse,
      alertThresholdConfig
    );

    setAlertsTempSelected(selectedMap);

    setSelectAllAlertTypes(
      allTypes.length > 0 && Object.keys(selectedMap).length === allTypes.length
    );
    setAlertsOpenRoleId(roleModel.id);
  };

  const closeAlertsPopover = () => setAlertsOpenRoleId(null);

  const resetAlerts = () => {
    const roleModel = roleList.find((r) => r.id === alertsOpenRoleId);
    if (!roleModel) return;

    const model: RoleModel =
      typeof structuredClone === 'function'
        ? structuredClone(roleModel)
        : JSON.parse(JSON.stringify(roleModel));

    const sub = (model?.sub_feature_list ?? []).find(
      (x) => (x?.feature_name ?? '').toLowerCase() === 'alerts'
    );
    if (!sub) return;

    const allTypes = Array.from(
      new Set(
        alertThresholdConfig.flatMap((g) =>
          (g.alerts ?? []).map((a) => a.alert_type).filter(Boolean)
        )
      )
    );

    const nextSelected = buildSelectedThresholdValues(
      allTypes,
      alertThresholdConfig
    );

    sub.allowed_statuses = allTypes;

    showSpin();
    ChangeRolePermission(token, model)
      .then(fetchAllRoles)
      .then(() => {
        setAlertsTempSelected(nextSelected);
        setSelectAllAlertTypes(allTypes.length > 0);
      })
      .catch(unauthorize)
      .finally(hideSpin);
  };

  const applyAlerts = () => {
    const roleId = alertsOpenRoleId;
    if (!roleId) return;

    const nextTypes = Object.keys(alertsTempSelected);

    setRoleList((prev) =>
      prev.map((r) => {
        if (r.id !== roleId) return r;

        const model: RoleModel =
          typeof structuredClone === 'function'
            ? structuredClone(r)
            : JSON.parse(JSON.stringify(r));

        const sub = (model?.sub_feature_list ?? []).find(
          (x) => (x?.feature_name ?? '').toLowerCase() === 'alerts'
        );
        if (sub) sub.allowed_statuses = nextTypes;

        return model;
      })
    );

    const roleModel = roleList.find((r) => r.id === roleId);
    if (!roleModel) return;

    const model: RoleModel = JSON.parse(JSON.stringify(roleModel));
    const sub = (model?.sub_feature_list ?? []).find(
      (x) => x.feature_name.toLowerCase() === 'alerts'
    );
    if (sub) sub.allowed_statuses = nextTypes;

    showSpin();
    ChangeRolePermission(token, model)
      .then((res: ApiResponse) => {
        if (res?.data?.data?.is_success) {
          message.success(res?.data?.data?.message);
        }
      })
      .catch(unauthorize)
      .finally(() => {
        hideSpin();
        setAlertsOpenRoleId(null);
        fetchAllRoles();
      });
  };

  const openKpiPopover = (roleModel: RoleModel) => {
    const sub = (roleModel?.sub_feature_list ?? []).find(
      (x) => (x?.feature_name ?? '').toLowerCase() === 'home kpi charts'
    );

    const allowedIds: string[] = Array.isArray(sub?.allowed_statuses)
      ? sub!.allowed_statuses!
      : [];

    const ids = allowedIds.length
      ? allowedIds
      : kpiSelectAllDefault
        ? allKpiIds
        : [];

    setKpiTempSelected(ids);
    setSelectAllKpis(kpiTotalCount > 0 && ids.length === kpiTotalCount);
    setKpiOpenRoleId(roleModel.id);
  };

  const closeKpiPopover = () => setKpiOpenRoleId(null);

  const resetKpis = () => {
    const roleModel = roleList.find((r) => r.id === kpiOpenRoleId);
    if (!roleModel) return;

    const model: RoleModel =
      typeof structuredClone === 'function'
        ? structuredClone(roleModel)
        : JSON.parse(JSON.stringify(roleModel));

    const sub = (model?.sub_feature_list ?? []).find(
      (x) => (x?.feature_name ?? '').toLowerCase() === 'home kpi charts'
    );
    if (!sub) return;

    const apiValues = kpiSelectAllDefault ? allKpiIds : [];
    sub.allowed_statuses = apiValues;

    showSpin();
    ChangeRolePermission(token, model)
      .then(fetchAllRoles)
      .then(() => {
        setKpiTempSelected(apiValues);
        setSelectAllKpis(
          kpiTotalCount > 0 && apiValues.length === kpiTotalCount
        );
      })
      .catch(unauthorize)
      .finally(hideSpin);
  };

  const applyKpis = (idsOverride?: string[]) => {
    const roleId = kpiOpenRoleId;
    if (!roleId) return;

    const nextIds = idsOverride ?? kpiTempSelected;

    setRoleList((prev) =>
      prev.map((r) => {
        if (r.id !== roleId) return r;

        const model: RoleModel =
          typeof structuredClone === 'function'
            ? structuredClone(r)
            : JSON.parse(JSON.stringify(r));

        const sub = (model?.sub_feature_list ?? []).find(
          (x) => (x?.feature_name ?? '').toLowerCase() === 'home kpi charts'
        );
        if (sub) sub.allowed_statuses = nextIds;

        return model;
      })
    );

    const roleModel = roleList.find((r) => r.id === roleId);
    if (!roleModel) return;

    const model: RoleModel = JSON.parse(JSON.stringify(roleModel));
    const sub = (model?.sub_feature_list ?? []).find(
      (x) => (x?.feature_name ?? '').toLowerCase() === 'home kpi charts'
    );
    if (sub) sub.allowed_statuses = nextIds;

    showSpin();
    ChangeRolePermission(token, model)
      .then((res: ApiResponse) => {
        if (res?.data?.data?.is_success) {
          fetchAllRoles();
          message.success(res?.data?.data?.message);
        }
      })
      .catch(unauthorize)
      .finally(() => {
        hideSpin();
        setKpiOpenRoleId(null);
      });
  };

  const openKpiReportsPopover = (roleModel: RoleModel) => {
    const sub = (roleModel?.sub_feature_list ?? []).find(
      (x) => (x?.feature_name ?? '').toLowerCase() === 'home kpi reports'
    );

    const apiVals: string[] = Array.isArray(sub?.allowed_statuses)
      ? sub!.allowed_statuses!
      : [];

    const selectedUi = apiVals
      .map((apiKey) => kpiReportsIdToLabel.get(apiKey) ?? '')
      .filter(Boolean);

    setKpiReportsTempSelected(selectedUi);
    setKpiReportsOpenRoleId(roleModel.id);
  };

  const closeKpiReportsPopover = () => setKpiReportsOpenRoleId(null);

  const resetKpiReports = () => {
    const roleModel = roleList.find((r) => r.id === kpiReportsOpenRoleId);
    if (!roleModel) return;

    const model: RoleModel =
      typeof structuredClone === 'function'
        ? structuredClone(roleModel)
        : JSON.parse(JSON.stringify(roleModel));

    const sub = (model?.sub_feature_list ?? []).find(
      (x) => (x?.feature_name ?? '').toLowerCase() === 'home kpi reports'
    );
    if (!sub) return;

    sub.allowed_statuses = [];

    showSpin();
    ChangeRolePermission(token, model)
      .then(fetchAllRoles)
      .then(() => setKpiReportsTempSelected([]))
      .catch(unauthorize)
      .finally(hideSpin);
  };

  const applyKpiReports = () => {
    const roleModel = roleList.find((r) => r.id === kpiReportsOpenRoleId);
    if (!roleModel) return;

    const model: RoleModel = JSON.parse(JSON.stringify(roleModel));
    const sub = (model?.sub_feature_list ?? []).find(
      (x) => x.feature_name.toLowerCase() === 'home kpi reports'
    );

    if (sub) {
      const apiValues = kpiReportsTempSelected
        .map((label) => kpiReportsLabelToId.get(label))
        .filter(Boolean) as string[];

      sub.allowed_statuses = apiValues;
    }

    showSpin();
    ChangeRolePermission(token, model)
      .then((res: ApiResponse) => {
        if (res?.data?.data?.is_success) {
          fetchAllRoles();
          message.success(res?.data?.data?.message);
        }
      })
      .catch(unauthorize)
      .finally(() => {
        hideSpin();
        setKpiReportsOpenRoleId(null);
      });
  };

  const openEditRoleDrawer = (role: RoleModel) => {
    const formData: EditRoleFormData = {
      id: role.id,
      name: role.name,
      is_connected_with_anything: role.is_connected_with_anything,
      is_delete: RolesPermissions?.is_delete,
    };
    setFormOpenData(formData);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setFormOpenData(null);
    setDrawerOpen(false);
  };

  const ASIDE_EXPANDED_W = 272;
  const ASIDE_COLLAPSED_W = 48;
  const asideWidth =
    rolesInfoCollapsed && isDesktop ? ASIDE_COLLAPSED_W : ASIDE_EXPANDED_W;
  const canAddRole = Boolean(RolesPermissions?.is_create);
  // Keep Add role out of the 48px strip (overflow) and available on mobile.
  //const showAddInMain = canAddRole && (!isDesktop || rolesInfoCollapsed);
  //const showAddInAside = canAddRole && isDesktop && !rolesInfoCollapsed;

  return (
    <>
      <div className='rolesPermissionsLayout'>
        <div
          className='rolesPermissionsLayout__main'
          style={{
            paddingRight: isDesktop ? 28 : 0,
          }}
        >
          <Flex
            justify='space-between'
            align='center'
            style={{ marginBottom: 24 }}
          >
            <CustomTitle
              text={` Roles (${roleList?.length || 0})`}
              fontSize={20}
              fontWeight='700'
              lineHeight='28px'
              color='var(--text-primary)'
              marginBottom='0'
            />
            <Flex align='center' gap={12}>
              {isDefaultAdmin && !isMobile && showPreviewMode && (
                <CustomButton
                  gap={8}
                  icon={<CopyProfileIcon />}
                  iconPosition='left'
                  fontSize='12px'
                  fontWeight={600}
                  lineHeight='16px'
                  text={'Preview role'}
                  height='28px'
                  bgColor='#EEE'
                  color='black'
                  borderRadius='28px'
                  boxShadow='none'
                  onClick={() => dispatch(setShouldShowRolePreview(true))}
                />
              )}
              <AddButton
                addButtonWidth='85px'
                fontSize='12px'
                fontWeight='600'
                text='Add role'
                onClick={() => setDrawerOpen(true)}
              />
              {!isDesktop && (
                <InfoCircleOutlined
                  className='zl-custom-icon'
                  style={{ cursor: 'pointer' }}
                  onClick={() => setInfoDrawerOpen(true)}
                />
              )}
            </Flex>
          </Flex>

          <div className='p-6'>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              <SectionsRoles />
              <span
                style={{
                  fontSize: '14px',
                  lineHeight: '16px',
                  fontWeight: 600,
                }}
              >
                Sections
              </span>
            </div>

            <div className='rp-table-wrap'>
              <div
                style={{ height: 1, background: '#EDEDED', marginTop: 12 }}
              />

              <SectionsTable
                rows={sectionsRows as never}
                roles={roleList}
                permission={RolesPermissions}
                isBaseLocked={isBaseLocked}
                optionsForRow={optionsForSection}
                isDesktop={isDesktop}
                onChange={(feature, role, val) =>
                  updatePermission('SECTION', feature, role, val)
                }
                onEditRole={(role) => openEditRoleDrawer(role)}
                canEditRoleHeader={(index) =>
                  index >= 3 && !!RolesPermissions?.is_update
                }
                firstColumnWidth={FIRST_COL_WIDTH}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 600,
                  marginTop: 24,
                  marginBottom: 8,
                }}
              >
                <FeaturesRoles />
                <span
                  style={{
                    fontSize: '14px',
                    lineHeight: '16px',
                    fontWeight: 600,
                  }}
                >
                  Features
                </span>
              </div>

              <div
                style={{ height: 1, background: '#EDEDED', marginTop: 12 }}
              />

              <FeaturesTable
                alertsReady={alertsReady}
                isDesktop={isDesktop}
                permission={RolesPermissions}
                rows={featuresRows as never}
                allStatuses={shipmentStatusLabels as never}
                roles={roleList}
                isBaseLocked={isBaseLocked}
                optionsForRow={optionsForSubFeature}
                onChange={(feature, role, val) =>
                  updatePermission('FEATURE', feature, role, val)
                }
                onEditRole={(role) => openEditRoleDrawer(role)}
                canEditRoleHeader={(index) =>
                  index >= 3 && !!RolesPermissions?.is_update
                }
                onOpenStatus={(role) => openStatusPopover(role)}
                statusOpenRoleId={statusOpenRoleId}
                statusTempSelected={statusTempSelected}
                setStatusTempSelected={setStatusTempSelected}
                onStatusReset={resetStatus}
                onStatusApply={applyStatus}
                onStatusClose={closeStatusPopover}
                alertThreshold={alertThresholdConfig}
                alertsOpenRoleId={alertsOpenRoleId}
                alertsTempSelected={alertsTempSelected}
                setAlertsTempSelected={setAlertsTempSelected}
                selectAllAlertTypes={selectAllAlertTypes}
                setSelectAllAlertTypes={setSelectAllAlertTypes}
                onOpenAlerts={(role) => openAlertsPopover(role)}
                onAlertsReset={resetAlerts}
                onAlertsApply={applyAlerts}
                onAlertsClose={closeAlertsPopover}
                kpiOpenRoleId={kpiOpenRoleId}
                kpiTempSelected={kpiTempSelected}
                setKpiTempSelected={setKpiTempSelected}
                selectAllKpis={selectAllKpis}
                setSelectAllKpis={setSelectAllKpis}
                kpiTotalCount={kpiTotalCount}
                setKpiTotalCount={setKpiTotalCount}
                onOpenKpis={(role) => openKpiPopover(role)}
                onKpisReset={resetKpis}
                onKpisApply={applyKpis}
                onKpisClose={closeKpiPopover}
                kpiGroups={kpiGroups as never}
                kpiSelectAllDefault={kpiSelectAllDefault}
                shipmentStatusSelectAllDefault={shipmentStatusSelectAllDefault}
                // home kpi reports permissions
                kpiReportsLabels={kpiReportsLabels}
                kpiReportsOpenRoleId={kpiReportsOpenRoleId}
                kpiReportsTempSelected={kpiReportsTempSelected}
                setKpiReportsTempSelected={setKpiReportsTempSelected}
                onOpenKpiReports={(role) => openKpiReportsPopover(role)}
                onKpiReportsReset={resetKpiReports}
                onKpiReportsApply={applyKpiReports}
                onKpiReportsClose={closeKpiReportsPopover}
                kpiReportsSelectAllDefault={kpiReportsSelectAllDefault}
              />
            </div>
          </div>
        </div>

        {isDesktop ? (
          <div
            className={
              rolesInfoCollapsed
                ? 'rolesPermissionsLayout__aside rolesPermissionsLayout__aside--collapsed'
                : 'rolesPermissionsLayout__aside'
            }
            style={{ width: asideWidth, flex: `0 0 ${asideWidth}px` }}
          >
            {/* {showAddInAside && (
              <div className='rolesPermissionsLayout__addRole'>
                <AddButton
                  addButtonWidth='85px'
                  fontSize='12px'
                  fontWeight='600'
                  text='Add role'
                  onClick={() => setDrawerOpen(true)}
                />
              </div>
            )} */}
            {/* Height-only spacer (no wide button) so strip top matches expanded card
                without overflowing the 48px rail and creating page scroll. */}
            {rolesInfoCollapsed && canAddRole && (
              <div
                className='rolesPermissionsLayout__addRole rolesPermissionsLayout__addRole--spacer'
                aria-hidden
              />
            )}
            <div
              ref={infoCardRef}
              className={
                rolesInfoCollapsed
                  ? 'RolesAndPermissionsCard RolesAndPermissionsCard--collapsed'
                  : 'RolesAndPermissionsCard'
              }
              style={
                rolesInfoCollapsed && infoCardHeight
                  ? { height: infoCardHeight }
                  : undefined
              }
            >
              <RolesInfoText
                collapsed={rolesInfoCollapsed}
                onToggleCollapse={() => setRolesInfoCollapsed((v) => !v)}
              />
            </div>
          </div>
        ) : null}

        <Drawer
          className='roles-permission-drawer'
          title='Roles & Permissions'
          placement='right'
          open={infoDrawerOpen}
          onClose={() => setInfoDrawerOpen(false)}
        >
          <div className='RolesAndPermissionsCard RolesAndPermissions-mobile-card '>
            <RolesInfoText />
          </div>
        </Drawer>
      </div>

      <CommonDrawer open={drawerOpen} onClose={closeDrawer}>
        <AddNewRoleForm
          form={roleForm}
          onClose={closeDrawer}
          editData={formOpenData}
          fetchAllRoles={fetchAllRoles}
        />
      </CommonDrawer>
    </>
  );
};

export default RolesAndPermissions;
